import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { getApiUrl } from '../config';

const ThreadCard = ({ thread, showToast, openAuthModal, isSavedProp, onSaveToggle }) => {
  const { user, token } = useAuth();
  const [voteCount, setVoteCount] = useState(thread.voteCount ?? 0);
  const [userVote, setUserVote] = useState(null); // 'up', 'down', or null
  const [isSaved, setIsSaved] = useState(isSavedProp || false);

  // Sync voteCount when thread prop changes
  useEffect(() => {
    setVoteCount(thread.voteCount ?? 0);
  }, [thread.voteCount, thread._id]);

  // Sync isSaved with prop
  useEffect(() => {
    setIsSaved(isSavedProp || false);
  }, [isSavedProp]);

  const getInitials = (name) => {
    if (!name) return 'U';
    const names = name.split(' ');
    if (names.length >= 2) {
      return names[0][0] + names[1][0];
    }
    return name.substring(0, 2).toUpperCase();
  };

  const handleVote = async (type) => {
    if (!user) {
      showToast('Sign in to vote');
      openAuthModal && openAuthModal('signin');
      return;
    }
    
    // Store previous state for rollback
    const previousVote = userVote;
    const previousCount = voteCount;
    
    // Calculate new state
    let newVote = type;
    let delta = 0;
    
    if (userVote === type) {
      // Clicking same vote = remove it
      newVote = null;
      delta = type === 'up' ? -1 : 1;
    } else if (userVote === null) {
      // New vote
      delta = type === 'up' ? 1 : -1;
    } else {
      // Switching vote (was opposite)
      delta = type === 'up' ? 2 : -2;
    }
    
    // Optimistic update
    setUserVote(newVote);
    setVoteCount(prev => prev + delta);
    
    try {
      const response = await fetch(getApiUrl(`/api/threads/${thread._id}/vote`), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ vote: newVote || 'remove' })
      });
      
      if (response.ok) {
        const data = await response.json();
        // Use server's authoritative vote count
        if (typeof data.voteCount === 'number') {
          setVoteCount(data.voteCount);
        }
      } else {
        // Revert on error
        setUserVote(previousVote);
        setVoteCount(previousCount);
        showToast('Failed to vote');
      }
    } catch (error) {
      // Revert on error
      setUserVote(previousVote);
      setVoteCount(previousCount);
      showToast('Failed to vote');
    }
  };

  const handleSave = async () => {
    if (!user) {
      showToast('Sign in to save posts');
      openAuthModal && openAuthModal('signin');
      return;
    }
    
    // Optimistic update
    const newSavedState = !isSaved;
    setIsSaved(newSavedState);
    
    try {
      const response = await fetch(getApiUrl(`/api/users/save/${thread._id}`), {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        setIsSaved(data.saved);
        showToast(data.saved ? 'Post saved' : 'Post unsaved');
        // Notify parent component
        if (onSaveToggle) {
          onSaveToggle(thread._id, data.saved);
        }
      } else {
        setIsSaved(!newSavedState);
        showToast('Failed to save');
      }
    } catch (error) {
      setIsSaved(!newSavedState);
      showToast('Failed to save');
    }
  };

  const getTagClass = (type) => {
    const classes = {
      question: 'tag-question',
      discussion: 'tag-discussion',
      project: 'tag-project',
      experiment: 'tag-experiment'
    };
    return classes[type] || 'tag-discussion';
  };

  const getBadgeClass = (role) => {
    const classes = {
      expert: 'expert',
      maintainer: 'maintainer'
    };
    return classes[role] || '';
  };

  const formatTime = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diff = now - date;
    const hours = Math.floor(diff / (1000 * 60 * 60));
    const days = Math.floor(hours / 24);
    
    if (hours < 1) return 'just now';
    if (hours < 24) return `${hours}h ago`;
    if (days < 7) return `${days}d ago`;
    return date.toLocaleDateString();
  };

  const formatCount = (count) => {
    if (count >= 1000) return `${(count / 1000).toFixed(1)}k`;
    return count;
  };

  return (
    <article className="thread-card">
      <div className="thread-header">
        <div className="thread-votes">
          <button 
            className={`vote-btn ${userVote === 'up' ? 'active' : ''}`} 
            onClick={() => handleVote('up')}
          >
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
              <polyline points="18 15 12 9 6 15"/>
            </svg>
          </button>
          <span className="vote-count">{voteCount}</span>
          <button 
            className={`vote-btn ${userVote === 'down' ? 'active' : ''}`} 
            onClick={() => handleVote('down')}
          >
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
              <polyline points="6 9 12 15 18 9"/>
            </svg>
          </button>
        </div>
        <div className="thread-content">
          <div className="thread-meta">
            <span className={`thread-tag ${getTagClass(thread.type)}`}>
              {thread.type?.charAt(0).toUpperCase() + thread.type?.slice(1) || 'Discussion'}
            </span>
            {thread.currentVersion && (
              <span className="thread-version">{thread.currentVersion}</span>
            )}
          </div>
          <Link to={`/thread/${thread._id}`} className="thread-title">
            {thread.title}
          </Link>
          <p className="thread-excerpt">{thread.excerpt || thread.content?.substring(0, 200)}</p>
          
          {thread.codeBlocks && thread.codeBlocks.length > 0 && (
            <div className="thread-code-preview">
              <div className="code-preview-header">
                <span className="code-preview-lang">{thread.codeBlocks[0].language || 'code'}</span>
              </div>
              <div className="code-preview-content">
                {thread.codeBlocks[0].code?.substring(0, 200)}
              </div>
            </div>
          )}
          
          <div className="thread-footer">
            <div className="thread-author">
              <div className="author-avatar">
                {thread.author?.avatar || getInitials(thread.author?.username)}
              </div>
              <div className="author-info">
                <span className="author-name">{thread.author?.username || 'Anonymous'}</span>
                {thread.author?.role && thread.author.role !== 'user' && (
                  <span className={`author-badge ${getBadgeClass(thread.author.role)}`}>
                    {thread.author.role.charAt(0).toUpperCase() + thread.author.role.slice(1)}
                  </span>
                )}
              </div>
              <span className="thread-time">• {formatTime(thread.createdAt)}</span>
            </div>
            <div className="thread-stats">
              <span className="stat-item">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/>
                </svg>
                {thread.replyCount || 0}
              </span>
              <span className="stat-item">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/>
                  <circle cx="12" cy="12" r="3"/>
                </svg>
                {formatCount(thread.views || 0)}
              </span>
              <button 
                className="stat-item" 
                onClick={handleSave}
                style={{ 
                  cursor: 'pointer', 
                  background: 'none', 
                  border: 'none',
                  color: isSaved ? 'var(--accent)' : 'var(--text-tertiary)'
                }}
              >
                <svg viewBox="0 0 24 24" fill={isSaved ? 'currentColor' : 'none'} stroke="currentColor" strokeWidth="2">
                  <path d="M19 21l-7-5-7 5V5a2 2 0 0 1 2-2h10a2 2 0 0 1 2 2z"/>
                </svg>
              </button>
            </div>
          </div>
        </div>
      </div>
    </article>
  );
};

export default ThreadCard;

